import axios from 'axios';
import { toast } from 'react-toastify';
import { apiURL } from './apiCalls';

/**
 * Payment Service Layer for handling all payment-related API calls
 * Following the existing API pattern in the codebase
 */

// Payment API Endpoints
const PAYMENT_ENDPOINTS = {
  GET_PAYMENT_REQUESTS: '/api/v1/layout_payments/my/requests/all',
  CREATE_PAYMENT_REQUEST: '/api/v1/payments/create-request',
  PROCESS_PAYMENT: '/api/v1/payments/process',
  UPDATE_PAYMENT_STATUS: '/api/v1/payments/update-status',
  GET_PAYMENT_HISTORY: '/api/v1/payments/history',
  GET_PAYMENT_METHODS: '/api/v1/payments/methods',
  VERIFY_PAYMENT: '/api/v1/payments/verify'
};

/**
 * Generic API call handler with error management
 */
const handleApiCall = async (apiCall) => {
  try {
    const response = await apiCall();
    return response;
  } catch (error) {
    console.error('Payment API Error:', error);
    const err = error;
    
    if (err.response) {
      const status = err.response.status;
      
      switch (status) {
        case 403:
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user_details');
          toast.error('Session expired. Please login again.');
          break;
        case 400:
          toast.error(err.response.data?.message || 'Invalid payment request');
          break;
        case 422:
          toast.error(err.response.data?.message || 'Validation failed');
          break;
        case 429:
          toast.error('Too many payment requests. Please try again later.');
          break;
        case 500:
          toast.error('Payment service error. Please try again later.');
          break;
        default:
          toast.error(err.response.data?.message || 'An error occurred');
      }
    } else {
      toast.error('Network error. Please check your connection.');
    }
    
    return null;
  }
};

/**
 * Payment Service Class
 */
export class PaymentService {
  
  /**
   * Get all payment requests for the current user
   * @returns {Promise<Object|null>} API response or null
   */
  static async getPaymentRequests() {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + PAYMENT_ENDPOINTS.GET_PAYMENT_REQUESTS,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Get specific payment request by ID
   * @param {string} requestId - Payment request ID
   * @returns {Promise<Object|null>} API response or null
   */
  static async getPaymentRequest(requestId) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + PAYMENT_ENDPOINTS.GET_PAYMENT_REQUEST + `/${requestId}`,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Create a new payment request
   * @param {Object} paymentData - Payment request data
   * @returns {Promise<Object|null>} API response or null
   */
  static async createPaymentRequest(paymentData) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + PAYMENT_ENDPOINTS.CREATE_PAYMENT_REQUEST,
        data: paymentData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Process payment for a request
   * @param {string} requestId - Payment request ID
   * @param {Object} paymentData - Payment processing data
   * @returns {Promise<Object|null>} API response or null
   */
  static async processPayment(requestId, paymentData) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + PAYMENT_ENDPOINTS.PROCESS_PAYMENT,
        data: {
          request_id: requestId,
          ...paymentData
        },
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Update payment status
   * @param {string} requestId - Payment request ID
   * @param {string} status - New payment status
   * @param {string} transactionId - Transaction ID (optional)
   * @returns {Promise<Object|null>} API response or null
   */
  static async updatePaymentStatus(requestId, status, transactionId = null) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'PUT',
        url: apiURL + PAYMENT_ENDPOINTS.UPDATE_PAYMENT_STATUS,
        data: {
          request_id: requestId,
          status: status,
          transaction_id: transactionId
        },
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Get payment history for the current user
   * @param {Object} filters - Optional filters (date range, status, etc.)
   * @returns {Promise<Object|null>} API response or null
   */
  static async getPaymentHistory(filters = {}) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + PAYMENT_ENDPOINTS.GET_PAYMENT_HISTORY,
        params: filters,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Get available payment methods
   * @returns {Promise<Object|null>} API response or null
   */
  static async getPaymentMethods() {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + PAYMENT_ENDPOINTS.GET_PAYMENT_METHODS,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Verify payment transaction
   * @param {string} transactionId - Transaction ID to verify
   * @returns {Promise<Object|null>} API response or null
   */
  static async verifyPayment(transactionId) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + PAYMENT_ENDPOINTS.VERIFY_PAYMENT,
        data: { transaction_id: transactionId },
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }
}

/**
 * Payment utility functions
 */
export const PaymentUtils = {
  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: INR)
   * @returns {string} Formatted currency string
   */
  formatCurrency: (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  },

  /**
   * Get payment status color for UI
   * @param {string} status - Payment status
   * @returns {string} Color code
   */
  getStatusColor: (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return '#ff9800';
      case 'processing':
        return '#2196f3';
      case 'completed':
      case 'paid':
        return '#4caf50';
      case 'failed':
      case 'cancelled':
        return '#f44336';
      case 'refunded':
        return '#9c27b0';
      default:
        return '#757575';
    }
  },

  /**
   * Get payment status text for UI
   * @param {string} status - Payment status
   * @returns {string} Human readable status
   */
  getStatusText: (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pending Payment';
      case 'processing':
        return 'Processing';
      case 'completed':
      case 'paid':
        return 'Payment Completed';
      case 'failed':
        return 'Payment Failed';
      case 'cancelled':
        return 'Payment Cancelled';
      case 'refunded':
        return 'Payment Refunded';
      default:
        return 'Unknown Status';
    }
  },

  /**
   * Check if payment is in a final state
   * @param {string} status - Payment status
   * @returns {boolean} True if payment is completed, failed, or cancelled
   */
  isFinalStatus: (status) => {
    const finalStatuses = ['completed', 'paid', 'failed', 'cancelled', 'refunded'];
    return finalStatuses.includes(status?.toLowerCase());
  },

  /**
   * Calculate payment due date
   * @param {string} createdAt - Payment request creation date
   * @param {number} days - Number of days to add (default: 7)
   * @returns {Date} Due date
   */
  calculateDueDate: (createdAt, days = 7) => {
    const created = new Date(createdAt);
    const due = new Date(created);
    due.setDate(due.getDate() + days);
    return due;
  },

  /**
   * Check if payment is overdue
   * @param {string} createdAt - Payment request creation date
   * @param {number} days - Number of days allowed (default: 7)
   * @returns {boolean} True if payment is overdue
   */
  isOverdue: (createdAt, days = 7) => {
    const dueDate = PaymentUtils.calculateDueDate(createdAt, days);
    return new Date() > dueDate;
  }
};

export default PaymentService;
